import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Edit as EditIcon } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import backend from "../../../../api";
import { useHistory } from "react-router-dom";
import { setDefaultPayment } from "../../../../redux-store/paymentMethod/action";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const convertToNormalString = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
};

const PaymentMethod = ({ register, redirect }) => {
  const [tabValue, setTabValue] = useState(0);
  const [bankAccountNo, setBankAccountNo] = useState("");
  const [eMoneyAccountNo, setEMoneyAccountNo] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [existingMethods, setExistingMethods] = useState({ methods: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editAccountNo, setEditAccountNo] = useState("");
  const { user } = useSelector((state) => state.auth);
  const history = useHistory();
  const dispatch = useDispatch();

  const baseUrl =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_FRONTEND
      : process.env.REACT_APP_FRONTEND_DEV;

  const fetchPaymentMethods = async (id) => {
    try {
      const response = await backend.get(`/payment-method?user=${id}`);
      const { paymentMethods } = response.data;
      console.log("paymentMethods: ", paymentMethods);
      if (paymentMethods && paymentMethods.methods.length > 0) {
        setExistingMethods(paymentMethods);
        if (paymentMethods.selectedPaymentType) {
          setSelectedPaymentType(paymentMethods.selectedPaymentType);
        }
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      toast.error("Failed to fetch payment methods.");
    }
  };

  useEffect(() => {
    if (user?.user?._id) {
      fetchPaymentMethods(user?.user?._id);
    }
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const saveAccount = async () => {
    if (isSaving) return;

    setIsSaving(true);
    const accountNo = tabValue === 0 ? bankAccountNo : eMoneyAccountNo;
    const receivingType = tabValue === 0 ? "bank_account" : "e_money";

    try {
      await backend.post("/payment-method", {
        user: user.user._id,
        accountNo,
        receivingType,
        redirectUrl: history?.location?.pathname,
      });
      toast.success(`${receivingType} added successfully`);

      setExistingMethods((prev) => ({
        ...prev,
        methods: [...prev.methods, { receivingType, accountNo }],
      }));

      if (receivingType === "bank_account") {
        setTabValue(1);
      }

      setIsSaving(false);
    } catch (error) {
      console.error(`Error saving ${receivingType}:`, error);
      toast.error(`Failed to save ${receivingType}`);
      setIsSaving(false);
    }
  };

  const saveDefaultMethod = async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      await backend.post("/payment-method", {
        user: user.user._id,
        selectedPaymentType,
      });

      dispatch(setDefaultPayment(selectedPaymentType));
      toast.success("Default payment type updated successfully.");

      if (redirect) {
        const redirectUrl = `/dashboard/${user?.user?.role}/payment-method`;
        history.push(redirectUrl);
      }

      setIsSaving(false);
    } catch (error) {
      console.error("Error updating default payment type:", error);
      toast.error("Failed to update default payment type.");
      setIsSaving(false);
    }
  };

  const saveUpdatedAccount = async (receivingType) => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      await backend.put("/payment-method/update-account", {
        user: user.user._id,
        receivingType,
        newAccountNo: editAccountNo,
        redirectUrl: history?.location?.pathname,
      });
      toast.success("Account number updated successfully.");

      // setExistingMethods((prev) => ({
      //   ...prev,
      //   methods: prev.methods.map((method) =>
      //     method.receivingType === receivingType
      //       ? { ...method, accountNo: editAccountNo }
      //       : method
      //   ),
      // }));

      fetchPaymentMethods(user?.user?._id);

      setIsEditing(false);
      setIsSaving(false);
    } catch (error) {
      console.error("Error updating account number:", error);
      toast.error("Failed to update account number.");
      setIsSaving(false);
    }
  };

  const renderAccountDetails = (receivingType) => {
    const account = existingMethods.methods.find(
      (method) => method.receivingType === receivingType
    );
    return (
      account && (
        <div className="p-3 bg-light border border-secondary rounded mb-3">
          <Typography variant="subtitle1" className="fw-bold text-dark">
            {receivingType === "bank_account" ? "Bank Account" : "E-Money"} -
            Already Connected ({account?.verified ? "Verified" : "Not Verified"}
            )
          </Typography>
          <Box mt={1} display="flex" alignItems="center">
            <Typography variant="body2" className="text-dark">
              <strong>Account Number:</strong>{" "}
              {isEditing ? (
                <input
                  type="text"
                  value={editAccountNo}
                  onChange={(e) => setEditAccountNo(e.target.value)}
                  className="form-control d-inline w-auto ml-2"
                />
              ) : (
                <span className="ml-2">{account.accountNo}</span>
              )}
            </Typography>
            {!isEditing && (
              <IconButton
                size="small"
                onClick={() => {
                  setIsEditing(true);
                  setEditAccountNo(account.accountNo);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {isEditing && (
              <button
                onClick={() => saveUpdatedAccount(receivingType)}
                className="btn btn-solid ml-2"
                disabled={isSaving}
              >
                Save
              </button>
            )}

            {!account?.verified && !isEditing && (
              <Typography variant="body2" className="text-danger mt-2">
                Your account is not verified. Please check your email for the
                verification link.
              </Typography>
            )}
          </Box>
        </div>
      )
    );
  };

  const availableMethods = existingMethods.methods.map(
    (method) => method.receivingType
  );

  const verifiedAccounts = existingMethods.methods.filter(
    (method) => method.verified
  );
  console.log("verifiedAccounts: ", verifiedAccounts);

  return (
    <div className="dashboard p-4">
      <ToastContainer />
      <div className="box-account box-info">
        <div className="box-head">
          <h2 className="text-dark">Manage Payment Method</h2>
        </div>
        <div className="row mt-3">
          <div className="col-lg-12 mx-auto register-page section-b-space">
            <div className="theme-card">
              <form className="theme-form">
                <Box className="border-bottom pb-2 mb-3">
                  <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Bank Account" {...a11yProps(0)} />
                    <Tab label="E-Money" {...a11yProps(1)} />
                  </Tabs>
                </Box>
                <CustomTabPanel value={tabValue} index={0}>
                  {renderAccountDetails("bank_account") || (
                    <>
                      <Typography className="text-secondary">
                        Account Type: <strong>Bank Account</strong>
                      </Typography>
                      <div className="form-group mb-3">
                        <label htmlFor="bankAccountNo">Account Number</label>
                        <input
                          type="text"
                          value={bankAccountNo}
                          className="form-control"
                          onChange={(e) => setBankAccountNo(e.target.value)}
                          placeholder="Enter account number"
                        />
                        <button
                          onClick={saveAccount}
                          className="btn btn-solid mt-2"
                          disabled={isSaving}
                        >
                          Save
                        </button>
                      </div>
                    </>
                  )}
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={1}>
                  {renderAccountDetails("e_money") || (
                    <>
                      <Typography className="text-secondary">
                        Account Type: <strong>E-Money</strong>
                      </Typography>
                      <div className="form-group mb-3">
                        <label htmlFor="eMoneyAccountNo">Account Number</label>
                        <input
                          type="text"
                          value={eMoneyAccountNo}
                          className="form-control"
                          onChange={(e) => setEMoneyAccountNo(e.target.value)}
                          placeholder="Enter account number"
                        />
                        <button
                          onClick={saveAccount}
                          className="btn btn-solid mt-2"
                          disabled={isSaving}
                        >
                          Save
                        </button>
                      </div>
                    </>
                  )}
                </CustomTabPanel>
                <hr />
                <div className="mt-2">
                  <label htmlFor="payment_method" className="fw-bold">
                    Select Default Method
                  </label>
                  <select
                    id="payment_method"
                    value={selectedPaymentType || ""}
                    className="form-control mb-3"
                    onChange={(e) => setSelectedPaymentType(e.target.value)}
                    disabled={verifiedAccounts.length < 1}
                  >
                    <option value="" disabled>
                      Select payment method
                    </option>
                    {verifiedAccounts.map((method, index) => {
                      return (
                        <option value={method.receivingType} key={index}>
                          {convertToNormalString(method.receivingType)}
                        </option>
                      );
                    })}
                    {/* {availableMethods.includes("bank_account") && (
                      <option value="bank_account">Bank Account</option>
                    )}
                    {availableMethods.includes("e_money") && (
                      <option value="e_money">E-Money</option>
                    )} */}
                  </select>
                  {selectedPaymentType && (
                    <button
                      onClick={saveDefaultMethod}
                      className="btn btn-solid"
                      disabled={isSaving}
                    >
                      Save
                    </button>
                  )}
                </div>
              </form>
              {selectedPaymentType && (
                <div className="mt-4 p-3 bg-light border rounded text-center">
                  <Typography
                    variant="subtitle1"
                    className="fw-bold text-secondary"
                  >
                    Current Selected Payment Type:
                  </Typography>
                  <Typography variant="h6" className="text-primary">
                    {selectedPaymentType === "bank_account"
                      ? "Bank Account"
                      : "E-Money"}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;
